<!-- Copyright (c) 2009-2021. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
  <div class="panel">
    <div style="width: 100%;">
      <div class="content-title-wrapper">
        <label class="hierarchy-label">{{ $t('storage') }}</label>

        <div v-if="storageStats === null" class="storage-stats-wrapper">
          <span class="storage-text">{{ $t('loading')}}...</span>
        </div>
        <div class="storage-stats-wrapper" v-else-if="storageStats">
          <div class="storage-stats-info">
            <span class="storage-text">{{ filesize(storageStats.used) }} / {{ filesize(storageStats.total) }}</span>
            <span class="storage-percentage"> ({{ Math.round(storageStats.usedP * 100) }}%)</span>
          </div>
          <div class="storage-progress-container">
            <progress 
              class="progress is-small" 
              :class="storageStats ? (storageStats.usedP > 0.9 ? 'is-danger' : storageStats.usedP > 0.7 ? 'is-warning' : 'is-info') : 'is-danger'"
              :value="storageStats ? storageStats.used : 100" 
              :max="storageStats ? storageStats.total : 100">
              {{ storageStats ? Math.round(storageStats.usedP * 100) : 0 }}%
            </progress>
          </div>
        </div>
        <div v-else class="storage-stats-wrapper">
          <span class="storage-text">{{ $t('statistics-unavailable') }}</span>
        </div>

        <div class="search-wrapper">
          <b-input
            :value="searchString"
            @input="debounceSearchString"
            class="search-uploaded-file"
            :placeholder="$t('search-placeholder')"
            icon="search"
          />
        </div>
        <div class="table-tool-icon-bar">
          <i class="fas fa-trash-alt icon-style" @click="confirmSelectedDeletion()" v-show="checkedRows && checkedRows.length > 0" ></i>
        </div>
      </div>

      <div class="storage-table-wrapper">
        <div>
          <cytomine-table
            :collection="uploadedFileCollection"
            sort="created" order="desc"
            :revision="revision"
            :refreshInterval="tableRefreshInterval"
            :openedDetailed.sync="openedDetails"
            :detailed="false"
            :checkable="true"
            :checkedRows.sync="checkedRows"
          >
      <template #default="{row: uFile}">
        <b-table-column :label="$t('preview')" width="80" class="image-overview">
          <image-thumbnail v-if="uFile.thumbURL" :url="uFile.thumbURL" :size="128" :key="uFile.thumbURL" :extra-parameters="{Authorization: 'Bearer ' + shortTermToken }"/>
          <div v-else class="is-size-7 has-text-grey">{{$t('no-preview-available')}}</div>
        </b-table-column>

        <b-table-column field="originalFilename" :label="$t('filename')" sortable width="200">
          {{ uFile.originalFilename }}
        </b-table-column>

        <b-table-column :label="$t('case')" sortable width="200">
          <template v-if="uFile.projects">
            <span class="tag is-info is-light" v-for="projectName in uFile.projects.split(', ')" :key="projectName" style="margin-right: 5px;">
              {{ projectName }}
            </span>
          </template>
          <template v-else>
            <span class="has-text-grey-light"><em>{{ $t('no-project') }}</em></span>
          </template>
        </b-table-column>

        <b-table-column field="created" :label="$t('created')" sortable width="150">
          {{ Number(uFile.created) | moment('lll') }}
        </b-table-column>

        <b-table-column field="size" :label="$t('size')" sortable width="80">
          {{ filesize(uFile.size) }}
        </b-table-column>

        <b-table-column field="status" :label="$t('status')" sortable width="80">
          <uploaded-file-status :file="uFile" />
        </b-table-column>

        <b-table-column label="" width="120">
          <div class="buttons is-right">
            <a class="button is-small is-link" @click="download(uFile.downloadURL)" v-if="uFile.status >= 100">
              {{$t('button-download')}}
            </a>
            <button class="button is-small is-danger" @click="confirmDeletion(uFile)">
              {{$t('button-delete')}}
            </button>
          </div>
        </b-table-column>

      </template>

          <template #empty>
            <div class="content has-text-grey has-text-centered">
              <p>{{$t('no-uploaded-file')}}</p>
            </div>
          </template>
          </cytomine-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {get} from '@/utils/store-helpers';
import ImageThumbnail from '@/components/image/ImageThumbnail';
import {UploadedFileCollection, UploadedFile, Cytomine} from 'cytomine-client';
import filesize from 'filesize';
import _ from 'lodash';
import CytomineTable from '@/components/utils/CytomineTable';
import UploadedFileStatusComponent from './UploadedFileStatus';
import {appendShortTermToken} from '@/utils/token-utils';

export default {
  name: 'list-uploaded-files',
  components: {
    CytomineTable,
    ImageThumbnail,
    'uploaded-file-status': UploadedFileStatusComponent
  },
  data() {
    return {
      loading: false,
      searchString: '',
      openedDetails: [],
      checkedRows: [],
      storageStats: null,
    };
  },
  props: {
    tableRefreshInterval: {
      type: Number,
      default: 0
    },
    revision : {type: Number, default: 0}
  },
  computed: {
    currentUser: get('currentUser/user'),
    users: get('currentStorage/users'),
    shortTermToken: get('currentUser/shortTermToken'),
    uploadedFileCollection() {
      return new UploadedFileCollection({
        onlyRootsWithDetails: true,
        originalFilename: {ilike: encodeURIComponent(this.searchString)}
      });
    }
  },
  methods: {
    async fetchStorageStats() {
      try {
        this.storageStats = await Cytomine.instance.fetchStorageStats();
      }
      catch(error) {
        console.log('Failed to fetch storage stats:', error);
        this.storageStats = false; // 실패 시
      }
    },
    filesize(size) {
      return (size) ? filesize(size, {base: 2}) : null;
    },
    debounceSearchString: _.debounce(async function(value) {
      this.searchString = value;
    }, 500),
    updatedTree() {
      this.$emit('update:revision', this.revision + 1); // updating the table will result in new files objects => the uf details will also be updated
    },
    download(url) {
      window.location.assign(appendShortTermToken(url, this.shortTermToken));
    },
    confirmDeletion(uFile) {
      this.$buefy.dialog.confirm({
        title: this.$t('confirm-deletion'),
        message: this.$t('confirm-deletion-file'),
        type: 'is-danger',
        confirmText: this.$t('button-confirm'),
        cancelText: this.$t('button-cancel'),
        onConfirm: () => this.deleteFile(uFile)
      });
    },
    async deleteFile(uFile) {
      try {
        await UploadedFile.delete(uFile.id);
        this.updatedTree();
      }
      catch(error) {
        console.log(error);
        let errorValues = error.response.data.errorValues;
        let text;
        if (error.response.status === 403) {
          if (errorValues && errorValues.projectNames && errorValues.imageNames) {
            text = this.$t('notif-error-delete-used-uploaded-file', {
              projects: errorValues.projectNames.join(', '),
              names: errorValues.imageNames.join(', ')
            });
          }
          else {
            text = this.$t('notif-error-delete-uploaded-file-forbidden');
          }
        }
        else {
          text = this.$t('notif-error-delete-uploaded-file');
        }
        this.$notify({type: 'error', text});
      }
    },
    async confirmSelectedDeletion() {
      let selectedNames = this.checkedRows.map(row => `- ${row.originalFilename}`).join('<br>');

      this.$buefy.dialog.confirm({
        title: this.$t('confirm-deletion'),
        message: this.$t('confirm-delete-selected-file', {selectedNames}),
        type: 'is-danger',
        confirmText: this.$t('button-confirm'),
        cancelText: this.$t('button-cancel'),
        onConfirm: () => this.deleteSelectedFiles()
      });
    },

    async deleteSelectedFiles() {
      const deletePromises = this.checkedRows.map(checkedRow =>
        UploadedFile.delete(checkedRow.id)
          .then(result => ({status: 'fulfilled', result, imageName: checkedRow.originalFilename}))
          .catch(error => ({status: 'rejected', error, imageName: checkedRow.originalFilename}))
      );
      const results = await Promise.allSettled(deletePromises);

      let failResultList = [];
      for (let result of results) {
        let value = result.value;
        if (value.status !== 'fulfilled') {
          failResultList.push(value);
          console.log('Failed to delete:', value.imageName, value);
        }
      }

      this.imageNameNotif(this.checkedRows, failResultList);
      this.updatedTree();
    },
    
    imageNameNotif(totalRowList, failResultList) {
      let successText = '';
      let failText = '';

      for (let item of totalRowList) {
        let failInfo;
        for (let i = 0; i < failResultList.length; i++) {
          if (failResultList[i].imageName === item.originalFilename) {
            failInfo = failResultList[i];
            failResultList.splice(i, 1); // i번째 요소 제거
            break;
          }
        }

        if (failInfo) {
          const failFilename = failInfo.imageName;
          const failValue = failInfo.error.response.data.errorValues;
          const failStatus = failInfo.error.response.status;

          if (failStatus === 403) {
            if (failValue.projectNames && failValue.imageNames) {
              failText += this.$t('notify-result-selected-delete-fail-included', {imageName: failFilename, projectNames: failValue.projectNames.join(', ')});
            }
            else {
              failText += this.$t('notify-result-selected-delete-fail-forbidden', {imageName: failFilename});
            }
          }
          else {
            failText += this.$t('notify-result-selected-delete-fail-unknown', {imageName: failFilename});
          }
        }
        else {
          successText += `- ${item.originalFilename}<br>`;
        }
      }

      let resultText = '';
      if (successText && !failText) {
        resultText = this.$t('notify-result-selected-delete-success', {successText});
        this.$notify({type: 'success', text: resultText});
      }
      else if (!successText && failText) {
        resultText = this.$t('notify-result-selected-delete-fail', {failText});
        this.$notify({type: 'error', text: resultText});
      }
      else {
        resultText = this.$t('notify-result-selected-delete-combined', {successText, failText});
        this.$notify({type: 'error', text: resultText});
      }

      return;
    },
  },
  async created() {
    await this.fetchStorageStats();
  }
};
</script>

<style scoped lang="scss">
.content-title-wrapper {
  height: 75px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
}

.storage-stats-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-left: auto;
  min-width: 200px;
  align-self: anchor-center;
}

.storage-stats-info {
  font-size: 0.85rem;
  color: #4A4847;
  margin-bottom: 4px;
}

.storage-text {
  font-weight: 500;
}

.storage-progress-container {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 8px;
}

.storage-progress-container .progress {
  flex: 1;
  margin-bottom: 0;
  height: 8px;
}

.storage-percentage {
  font-size: 0.8rem;
  font-weight: 600;
  color: #4A4847;
  min-width: 40px;
  text-align: right;
}

.search-wrapper {
  flex: 1;
  margin-left: 12px;
  max-width: 15em;
  display: flex;
  justify-content: flex-end;
  align-self: anchor-center;
}

.storage-table-wrapper {
  min-width: 1200px;
}

.panel {
  display: flex;
  flex-direction: row;
}

.hierarchy-label {
  margin: 18px 10px;
  font-size: 1.3rem;
  color: #4A4847;
}

.table-tool-icon-bar {
  height: 40px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.icon-style {
  cursor: pointer;
  margin-right: 10px;
  padding: 10px;
}

.small-text {
  font-size: 0.9em;
}

.upload-table  {
  position: relative;
  bottom: 0.4em;
}

.upload-table td {
  vertical-align: middle !important;
}

.upload-table td:first-child {
  width: 20vw;
}

.upload-table td:nth-child(2) {
  width: 10em;
}

.upload-table td:nth-child(3) {
  width: 15vw;
}

.upload-table td:last-child .field {
  justify-content: flex-end;
}

.upload-table td:last-child .control {
  text-align: right;
}

.column.flex-column {
  flex-direction: column;
}

.progress:not(:last-child) {
  margin-bottom: 0.75em;
}

.column:first-child {
  padding-top: 1.25em;
}

.first-child-like {
  display: block;
  padding-top: 0.5em;
}

.image-overview {
  max-height: 4em;
  max-width: 6em;
}
</style>

<style>
.storage-wrapper .upload-draggable .button {
  margin-bottom: 0;
}

.storage-wrapper .upload-draggable {
  margin-right: 0.75em;
  position: relative;
  bottom: 4px;
}

.search-uploaded-file {
  width: 100%;
}
</style>

